import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { ResearchBlock } from "@/components/site/ResearchBlock";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar animation i Stockholm?",
    a: "Kortare motion graphics (15–30 sek) ligger på 25 000–60 000 kr. Animerad förklaringsfilm 60–90 sekunder kostar typiskt 60 000–150 000 kr. 3D-animation startar runt 120 000 kr. Löpande animationsflöde via abonnemang från 14 995 kr/mån.",
  },
  {
    q: "Hur lång tid tar en animation?",
    a: "Motion graphics 2–3 veckor från godkänt manus. Animerad förklaringsfilm 3–5 veckor inklusive storyboard, illustration och speaker. 3D-animation 5–8 veckor beroende på modellering.",
  },
  {
    q: "Behöver vi vara på plats i Stockholm?",
    a: "Nej. Animation produceras utan inspelningsdag, så hela processen kan skötas digitalt. Vi ses gärna fysiskt för brief och storyboard-genomgång i Stockholm när ni vill det.",
  },
  {
    q: "Kan ni animera i vår grafiska profil?",
    a: "Ja. Vi bygger animationen på er befintliga brand book — färger, typsnitt, ikonspråk och logotypanimation — så att materialet fungerar ihop med resten av er kommunikation.",
  },
  {
    q: "Vad passar bäst: animation eller filmad produktion?",
    a: "Animation passar när ni ska förklara något abstrakt, visa processer eller data, eller när det inte finns något fysiskt att filma. Filmad produktion vinner när trovärdighet, människor och miljöer är poängen.",
  },
];

export const Route = createFileRoute("/animation_/stockholm")({
  head: () => ({
    meta: [
      { title: "Animationsstudio Stockholm — se exempel, pris och tidplan" },
      {
        name: "description",
        content:
          "Motion graphics, animerad förklaringsfilm och 3D i Stockholm. Se exempel, godkänn bildmanus innan vi animerar och få fast pris — klart på 2–5 veckor.",
      },

      { property: "og:title", content: "Animationsstudio Stockholm — klart på 2–5 veckor" },
      {
        property: "og:description",
        content: "Motion graphics, förklaringsfilm och 3D-animation för Stockholmsbolag. Pris och leveranstider.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/animation/stockholm" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/animation/stockholm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/animation/stockholm", name: "Animationsstudio Stockholm — animerad film & motion | Fenny", description: "Animationsstudio i Stockholm: motion graphics, animerad förklaringsfilm och 3D. Bildmanus godkänns innan animation, leverans på 2–5 veckor till fast pris."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          localBusinessForCity({
            city: "Stockholm",
            path: "/animation/stockholm",
            description:
              "Animation och motion graphics i Stockholm — förklaringsfilm, 3D och animerad reklamfilm.",
            postalRegion: "Stockholms län",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Animation", path: "/motion-graphics" },
            { name: "Stockholm", path: "/animation/stockholm" },
          ]),
        ),
      },
    ],
  }),
  component: AnimationStockholmPage,
});

function AnimationStockholmPage() {
  return (
    <LandingPage
      eyebrow="Animation Stockholm"
      title={<>Animation i Stockholm — förklara det som är svårt att filma.</>}
      lede="Motion graphics, animerad förklaringsfilm och 3D för Stockholmsbolag som behöver visa processer, data eller produkter som inte finns framför en kamera. Fast pris, storyboard innan produktion och leverans i alla format."
      tldr={
        <p>
          Animation i Stockholm kostar typiskt <strong>25 000–150 000 kr</strong>{" "}
          beroende på längd och teknik, med leverans på <strong>2–5 veckor</strong>.
          Fenny producerar motion graphics, animerad förklaringsfilm och
          3D-animation — med storyboard och fast pris innan produktion startar.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="animation-stockholm"
      bullets={[
        { title: "Storyboard först", body: "Ni ser bildmanus och tonalitet innan en enda scen animeras." },
        { title: "Er grafiska profil", body: "Färger, typsnitt och ikonspråk hämtas från er brand book." },
        { title: "Alla format", body: "16:9, 9:16, 1:1 med undertexter — redo för LinkedIn, sajt och mässa." },
      ]}
      pricing={{
        heading: "Vad kostar animation i Stockholm?",
        intro: "Riktpriser exklusive moms.",
        rows: [
          { format: "Motion graphics 15–30 sek", scope: "Manus, design, animation, ljud", price: "25 000–60 000 kr", lead: "2–3 veckor" },
          { format: "Animerad förklaringsfilm 60–90 sek", scope: "Storyboard, illustration, speaker, ljudläggning", price: "60 000–150 000 kr", lead: "3–5 veckor" },
          { format: "3D-animation", scope: "Modellering, rendering, komposition", price: "från 120 000 kr", lead: "5–8 veckor" },
          { format: "Löpande animation", scope: "Abonnemang med kontinuerlig output", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
      }}
      sections={[
        localProductionSection("stockholm", "Stockholm"),
        {
          id: "jamforelse",
          heading: "Innan ni väljer",
          body: (
            <ResearchBlock
              intro={"Jämför ni animationsbyråer i Stockholm är det tre saker som avgör pris och kvalitet — inte var studion ligger."}
              steps={[
                { q: "Välj teknik", a: "2D motion graphics, illustrerad animation och 3D har olika prisbild och passar olika budskap." },
                { q: "Räkna på manus", a: "Ett tajt manus på 45 sekunder kostar mindre att animera än två minuter som säger samma sak." },
                { q: "Planera för återanvändning", a: "Be om en mall så kan ni göra fler filmer i samma stil senare till en bråkdel av priset." },
              ]}
              links={[
                { label: "Animerad förklaringsfilm", href: "/animerad-forklaringsfilm" },
                { label: "3D-animation", href: "/3d-animation" },
                { label: "Prisspann och exempel", href: "/priser" },
              ]}
            />
          ),
        },
        {
          id: "nar-animation",
          heading: "När animation slår filmad produktion",
          body: (
            <>
              <p>
                Animation vinner när det ni säljer är abstrakt: mjukvara, tjänster,
                processer, säkerhetsflöden eller data. Ni slipper location,
                skådespelare och väder — och kan uppdatera en scen utan att spela
                in om allt.
              </p>
              <p>
                Handlar det istället om trovärdighet, människor och miljöer bör ni
                titta på{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/stockholm">
                  filmproduktion i Stockholm
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "animationsstudio",
          heading: "Animationsstudio i Stockholm — vad det innebär",
          body: (
            <p>
              Söker ni &quot;animationsstudio Stockholm&quot; eller
              &quot;animationsbyrå&quot; menar de flesta samma sak: ett team som
              kan idé, design, animation och ljud i samma hus. Vi arbetar med
              2D, motion graphics och 3D, och kombinerar ofta animation med
              filmad bild — se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-reklamfilm">animerad reklamfilm</a>{" "}
              och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">motion graphics</a>.
            </p>
          ),
        },
        {
          id: "tekniker",
          heading: "Tekniker vi arbetar i",
          body: (
            <p>
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">Motion graphics</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-reklamfilm">animerad reklamfilm</a> och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/3d-animation">3D-animation</a>. Vi kombinerar ofta animation med filmade sekvenser i samma produktion.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      closing={{
        title: "Ska ni göra animation i Stockholm?",
        body: "Boka 30 minuter så skissar vi upp format, längd och prisnivå direkt i mötet.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
